import React, { useEffect, useState } from 'react';
import { consultationService, uploadService } from '../services/api';
import DoctorSketchesPanel from './DoctorSketchesPanel';
import './Dashboard.css';

const formatDate = (date) => {
  if (!date) return '-';
  return new Date(date).toLocaleDateString('es-MX', {
    day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'
  });
};

const Dashboard = ({
  user,
  activeDrafts = [],
  onResumeDraft,
  onNewConsultation,
  onViewPatients,
  onViewLaboratories,
  onViewAuditLog,
  onOpenCalibrationReceta
}) => {
  const [stats, setStats] = useState(null);
  const [loadingStats, setLoadingStats] = useState(true);
  const [cieQuery, setCieQuery] = useState('');
  const [cieResults, setCieResults] = useState([]);
  const [searchingCie, setSearchingCie] = useState(false);

  // Upload state
  const canUpload = user.rol === 'admin';
  const [uploads, setUploads] = useState([]);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadDesc, setUploadDesc] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadError, setUploadError] = useState('');
  const [uploadSuccess, setUploadSuccess] = useState('');

  useEffect(() => {
    const loadStats = async () => {
      try {
        setLoadingStats(true);
        const result = await consultationService.getStats();
        if (result.success) {
          setStats(result.data);
        }
      } catch (error) {
        console.error('Error loading stats:', error);
      } finally {
        setLoadingStats(false);
      }
    };
    loadStats();
  }, []);

  useEffect(() => {
    const trimmedQuery = cieQuery.trim();
    const looksLikeCode = /^[a-z]\d?/i.test(trimmedQuery);
    if (trimmedQuery.length < 2 && !looksLikeCode) {
      setCieResults([]);
      return;
    }

    const timer = setTimeout(async () => {
      try {
        setSearchingCie(true);
        const result = await consultationService.searchCIE10(trimmedQuery);
        setCieResults(result.data || []);
      } catch (error) {
        setCieResults([]);
      } finally {
        setSearchingCie(false);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [cieQuery]);

  // Load uploads if user has permission
  useEffect(() => {
    if (!canUpload) return;
    loadUploads();
  }, [canUpload]);

  const loadUploads = async () => {
    try {
      const result = await uploadService.list();
      if (result.success) setUploads(result.data);
    } catch (err) {
      console.error('Error loading uploads:', err);
    }
  };

  const handleUpload = async () => {
    if (!uploadFile) return;
    setUploading(true);
    setUploadProgress(0);
    setUploadError('');
    setUploadSuccess('');
    try {
      await uploadService.upload(uploadFile, uploadDesc, setUploadProgress);
      setUploadSuccess(`"${uploadFile.name}" subido correctamente`);
      setUploadFile(null);
      setUploadDesc('');
      // Reset file input
      const fileInput = document.getElementById('upload-file-input');
      if (fileInput) fileInput.value = '';
      await loadUploads();
    } catch (err) {
      setUploadError(err.response?.data?.message || 'Error al subir archivo');
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const handleDeleteUpload = async (id, nombre) => {
    if (!window.confirm(`Eliminar "${nombre}"?`)) return;
    try {
      await uploadService.delete(id);
      await loadUploads();
    } catch (err) {
      setUploadError('Error al eliminar archivo');
    }
  };

  const handleOpenUpload = async (upload) => {
    try {
      setUploadError('');
      await uploadService.open(upload.id, upload.nombre_original, true);
    } catch {
      setUploadError('Error al descargar archivo');
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  return (
    <div>
      <div className="container">
      <div className="dashboard-welcome">
        <h2 className="dashboard-welcome-title">Bienvenido, {user.nombre}</h2>
        <p className="dashboard-welcome-sub">Panel de Consulta Externa &mdash; Clínica de Especialidades Guarneros</p>
      </div>
      {/* Quick Stats */}
      <div className="stats-row">
        <div className="stat-card stat-card--blue">
          <div className="stat-card-icon" aria-hidden="true">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
              <circle cx="9" cy="7" r="4"/>
              <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
              <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
            </svg>
          </div>
          <div className="stat-number">{loadingStats ? <span className="stat-loading"/> : (stats?.patients_today ?? 0)}</div>
          <div className="stat-label">Pacientes hoy</div>
        </div>
        <div className="stat-card stat-card--teal">
          <div className="stat-card-icon" aria-hidden="true">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
              <circle cx="12" cy="7" r="4"/>
            </svg>
          </div>
          <div className="stat-number">{loadingStats ? <span className="stat-loading"/> : (stats?.total_patients ?? 0)}</div>
          <div className="stat-label">Pacientes totales</div>
        </div>
        <div className="stat-card stat-card--violet">
          <div className="stat-card-icon" aria-hidden="true">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
              <line x1="16" y1="2" x2="16" y2="6"/>
              <line x1="8" y1="2" x2="8" y2="6"/>
              <line x1="3" y1="10" x2="21" y2="10"/>
            </svg>
          </div>
          <div className="stat-number">{loadingStats ? <span className="stat-loading"/> : (stats?.consultations_this_week ?? 0)}</div>
          <div className="stat-label">Consultas esta semana</div>
        </div>
      </div>

      {/* Action Cards */}
      <div className="dashboard-actions">
        <div className="action-card" onClick={onNewConsultation}>
          <div className="action-card-accent" aria-hidden="true"/>
          <div className="action-icon">
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
              <polyline points="14 2 14 8 20 8"/>
              <line x1="12" y1="18" x2="12" y2="12"/>
              <line x1="9" y1="15" x2="15" y2="15"/>
            </svg>
          </div>
          <h3>Nueva Consulta</h3>
          <p>Registrar nueva consulta de paciente</p>
        </div>

        <div className="action-card" onClick={onViewPatients}>
          <div className="action-card-accent" aria-hidden="true"/>
          <div className="action-icon">
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
              <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
              <circle cx="9" cy="7" r="4"/>
              <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
              <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
            </svg>
          </div>
          <h3>Ver Pacientes</h3>
          <p>Consultar historial y expedientes</p>
        </div>

        {onViewLaboratories && (
          <div className="action-card" onClick={onViewLaboratories}>
            <div className="action-card-accent" aria-hidden="true"/>
            <div className="action-icon">
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                <path d="M9 3H5a2 2 0 0 0-2 2v4m6-6h10a2 2 0 0 1 2 2v4M9 3v11m0 0H5m4 0h4m6-11v11m0 0h-4m4 0h-6"/>
                <path d="M3 9h18"/>
                <path d="M3 15h18"/>
              </svg>
            </div>
            <h3>Laboratorios</h3>
            <p>Solicitudes y seguimiento de estudios</p>
          </div>
        )}

        {onViewAuditLog && (
          <div className="action-card" onClick={onViewAuditLog}>
            <div className="action-card-accent" aria-hidden="true"/>
            <div className="action-icon">
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                <path d="M12 8v4l3 3"/>
                <circle cx="12" cy="12" r="9"/>
              </svg>
            </div>
            <h3>Bitácora de Cambios</h3>
            <p>Historial de ediciones de consultas y pacientes</p>
          </div>
        )}

        {onOpenCalibrationReceta && (
          <div className="action-card" onClick={onOpenCalibrationReceta}>
            <div className="action-card-accent" aria-hidden="true"/>
            <div className="action-icon">
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                <polyline points="14 2 14 8 20 8"/>
                <line x1="16" y1="13" x2="8" y2="13"/>
                <line x1="16" y1="17" x2="8" y2="17"/>
                <polyline points="10 9 9 9 8 9"/>
              </svg>
            </div>
            <h3>Receta Demo</h3>
            <p>Abrir receta para ajustes visuales</p>
          </div>
        )}
      </div>

      {activeDrafts.length > 0 && (
        <div className="info-card active-drafts-card">
          <div className="active-drafts-header">
            <h4>Borradores activos</h4>
            <span className="active-drafts-count">{activeDrafts.length}</span>
          </div>
          <p className="active-drafts-subtitle">
            Consultas o recetas con avance pendiente para retomar desde el ltimo paso guardado.
          </p>
          <div className="active-drafts-list">
            {activeDrafts.slice(0, 5).map((draft) => (
              <button
                key={`${draft.session_key}-${draft.draft_consultation_id || 'pending'}`}
                type="button"
                className="active-draft-item"
                onClick={() => onResumeDraft && onResumeDraft(draft)}
                aria-label={`Continuar borrador de ${draft.patient_name}`}
              >
                <div className="active-draft-main">
                  <span className="active-draft-name">{draft.patient_name}</span>
                  <span className="active-draft-step">Paso {draft.current_step || 1}</span>
                </div>
                <div className="active-draft-meta">
                  <span className="active-draft-motivo">
                    {draft.motivo_consulta
                      ? (draft.motivo_consulta.length > 90
                        ? `${draft.motivo_consulta.slice(0, 90)}...`
                        : draft.motivo_consulta)
                      : 'Sin motivo capturado todavia'}
                  </span>
                  <span className="active-draft-time">{formatDate(draft.updated_at)}</span>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Bottom Section: Recent Activity + CIE Search */}
      <div className="dashboard-bottom">
        {/* Recent Activity */}
        <div className="info-card recent-activity-card">
          <h4>Actividad Reciente</h4>
          {loadingStats ? (
            <div className="activity-loading">
              <div className="spinner-small"></div>
              <span>Cargando...</span>
            </div>
          ) : stats?.recent_consultations?.length > 0 ? (
            <div className="recent-list">
              {stats.recent_consultations.map((item) => (
                <div key={item.id} className="recent-item">
                  <div className="recent-item-left">
                    <span className="recent-patient">{item.paciente_nombre}</span>
                    <span className="recent-motivo">{item.motivo_consulta ? (item.motivo_consulta.length > 60 ? item.motivo_consulta.slice(0, 60) + '...' : item.motivo_consulta) : 'Sin motivo registrado'}</span>
                  </div>
                  <div className="recent-item-right">
                    <span className="recent-doctor">Dr. {item.doctor_nombre}</span>
                    <span className="recent-date">{formatDate(item.fecha_consulta)}</span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="activity-empty">
              <p>No hay consultas recientes</p>
              <small>Las consultas aparecerán aquí conforme se registren</small>
            </div>
          )}
        </div>

        {/* CIE-10 Quick Search */}
        <div className="info-card">
          <h4>Buscador CIE-10</h4>
          <div className="cie-quick-search">
            <input
              type="text"
              value={cieQuery}
              onChange={(e) => setCieQuery(e.target.value)}
              placeholder="Buscar código o diagnóstico CIE..."
            />
            {searchingCie && <p className="cie-status">Buscando...</p>}
            {!searchingCie && cieQuery.trim().length > 0 && cieResults.length === 0 && (
              <p className="cie-status">Sin resultados</p>
            )}
            {cieResults.length > 0 && (
              <div className="cie-results-list">
                {cieResults.slice(0, 8).map((item) => (
                  <div key={item.codigo} className="cie-result-item">
                    <span className="cie-code">{item.codigo}</span>
                    <span className="cie-desc">{item.descripcion}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      <DoctorSketchesPanel user={user} />

      {/* Upload Section — only for authorized users */}
      {canUpload && (
        <div className="info-card" style={{ marginTop: '1.5rem' }}>
          <h4>Subir Archivos</h4>
          <p style={{ fontSize: '0.85rem', color: '#666', marginBottom: '1rem' }}>
            Suba archivos (Excel, PDF, imágenes, etc.) para construir la base de datos real.
          </p>

          {uploadError && (
            <div className="error-message" style={{ marginBottom: '0.75rem' }}>{uploadError}</div>
          )}
          {uploadSuccess && (
            <div style={{ background: '#d4edda', color: '#155724', padding: '0.5rem 0.75rem', borderRadius: '4px', marginBottom: '0.75rem', fontSize: '0.9rem' }}>
              {uploadSuccess}
            </div>
          )}

          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: '1rem' }}>
            <div style={{ flex: '1 1 200px' }}>
              <label htmlFor="upload-file-input" style={{ fontSize: '0.85rem', fontWeight: 500 }}>Archivo</label>
              <input
                id="upload-file-input"
                type="file"
                onChange={(e) => { setUploadFile(e.target.files[0] || null); setUploadError(''); setUploadSuccess(''); }}
                disabled={uploading}
                style={{ display: 'block', marginTop: '0.25rem', width: '100%' }}
              />
            </div>
            <div style={{ flex: '1 1 200px' }}>
              <label style={{ fontSize: '0.85rem', fontWeight: 500 }}>Descripción (opcional)</label>
              <input
                type="text"
                value={uploadDesc}
                onChange={(e) => setUploadDesc(e.target.value)}
                placeholder="Ej: Expedientes enero 2025"
                disabled={uploading}
                style={{ display: 'block', marginTop: '0.25rem', width: '100%', padding: '0.4rem' }}
              />
            </div>
            <button
              className="btn btn-primary"
              onClick={handleUpload}
              disabled={!uploadFile || uploading}
              style={{ height: 'fit-content', whiteSpace: 'nowrap' }}
            >
              {uploading ? `Subiendo ${uploadProgress}%` : 'Subir'}
            </button>
          </div>

          {uploading && (
            <div style={{ background: '#e9ecef', borderRadius: '4px', height: '6px', marginBottom: '1rem' }}>
              <div style={{ background: '#007bff', height: '100%', borderRadius: '4px', width: `${uploadProgress}%`, transition: 'width 0.3s' }} />
            </div>
          )}

          {uploads.length > 0 && (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                <thead>
                  <tr style={{ borderBottom: '2px solid #dee2e6', textAlign: 'left' }}>
                    <th style={{ padding: '0.5rem' }}>Archivo</th>
                    <th style={{ padding: '0.5rem' }}>Tamaño</th>
                    <th style={{ padding: '0.5rem' }}>Descripción</th>
                    <th style={{ padding: '0.5rem' }}>Fecha</th>
                    <th style={{ padding: '0.5rem' }}>Subido por</th>
                    <th style={{ padding: '0.5rem' }}>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {uploads.map((u) => (
                    <tr key={u.id} style={{ borderBottom: '1px solid #dee2e6' }}>
                      <td style={{ padding: '0.5rem', maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {u.nombre_original}
                      </td>
                      <td style={{ padding: '0.5rem', whiteSpace: 'nowrap' }}>{formatFileSize(u.tamano)}</td>
                      <td style={{ padding: '0.5rem' }}>{u.descripcion || '-'}</td>
                      <td style={{ padding: '0.5rem', whiteSpace: 'nowrap' }}>{formatDate(u.created_at)}</td>
                      <td style={{ padding: '0.5rem' }}>{u.subido_por_nombre}</td>
                      <td style={{ padding: '0.5rem', whiteSpace: 'nowrap' }}>
                        <button
                          type="button"
                          onClick={() => handleOpenUpload(u)}
                          style={{ background: 'none', border: 'none', marginRight: '0.5rem', color: '#007bff', textDecoration: 'none', cursor: 'pointer', padding: 0, fontSize: '0.85rem' }}
                        >
                          Descargar
                        </button>
                        <button
                          onClick={() => handleDeleteUpload(u.id, u.nombre_original)}
                          style={{ background: 'none', border: 'none', color: '#dc3545', cursor: 'pointer', padding: 0, fontSize: '0.85rem' }}
                        >
                          Eliminar
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
      </div>{/* /container */}
    </div>
  );
};

export default Dashboard;
